import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { filter, map, NEVER, Observable, of } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import { BadgeVariant, ButtonMode, IconPosition, TabTheme } from '@fcom/ui-components';
import { LoaderType } from '@fcom/common';
import { equals } from '@fcom/utils';

import { Category, FinnairBoundItem, FinnairCart, FinnairOrder } from '../../api/models';
import { FinnairServiceItemWithUpsell, Upsell, UpsellCategoriesForBound } from '../../interfaces';
import { isAdult } from '../../utils';

@Component({
  selector: 'fin-dapi-included-products',
  templateUrl: './included-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DapiIncludedProductsComponent implements OnInit, OnChanges {
  readonly Category = Category;
  readonly IconPosition = IconPosition;
  readonly LoaderType = LoaderType;
  readonly ButtonMode = ButtonMode;
  readonly BadgeVariant = BadgeVariant;
  readonly TabTheme = TabTheme;

  @Input() booking: FinnairCart | FinnairOrder;

  @Input() showTickets = false;

  @Input() showModifyButtons = false;

  @Input() showInformationModal = false;

  @Input() useCabinClass = false;

  @Input() titleTranslationKey;

  @Input() showAdditionalTitle = false;

  @Input() expandOpen = false;

  @Input() headerContentTranslationKey?: string;

  @Input() showUpsellCardForSeats = false;

  @Input() servicesToShow: FinnairServiceItemWithUpsell[];

  @Input() servicesReady$: Observable<boolean> = of(false);

  @Input() showAsPending = false;

  @Input() upsellInModal$: Observable<UpsellCategoriesForBound> = NEVER;

  @Output()
  modifyClicked: EventEmitter<Category> = new EventEmitter<Category>();

  @Output()
  upsellClicked: EventEmitter<Category> = new EventEmitter<Category>();

  servicesForBounds: (FinnairBoundItem & { services: FinnairServiceItemWithUpsell[] })[];
  headerContents$: Observable<string[]>;

  constructor(private languageService: LanguageService) {}

  ngOnInit(): void {
    this.updateServices();

    this.headerContents$ = this.headerContentTranslationKey
      ? this.languageService.translate(this.headerContentTranslationKey).pipe(
          map((content) => (Array.isArray(content) ? content : [content])),
          filter(Boolean),
          finShare()
        )
      : NEVER;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !equals(changes['showUpsellCardForSeats']?.currentValue, changes['showUpsellCardForSeats']?.previousValue) ||
      !equals(changes['servicesToShow']?.currentValue, changes['servicesToShow']?.previousValue) ||
      !equals(changes['booking']?.currentValue, changes['booking']?.previousValue)
    ) {
      this.updateServices();
    }
  }

  private updateServices(): void {
    const servicesToShow = this.getServicesToShow();
    this.servicesForBounds = this.booking.bounds.map((bound) => {
      const services = servicesToShow
        .map((service) => ({
          ...service,
          bounds: service.bounds.filter(({ id }) => id === bound.id),
        }))
        .filter(({ bounds }) => bounds.length > 0);

      return {
        ...bound,
        services,
      };
    });
  }

  private getServicesToShow(): FinnairServiceItemWithUpsell[] {
    if (!this.showUpsellCardForSeats) {
      return this.servicesToShow;
    }

    const articleKey =
      this.booking.passengers.filter(isAdult).length === 1
        ? 'MMB.fragments.upsell.seat.onePax.url'
        : 'MMB.fragments.upsell.seat.twoPax.url';

    const upsell: Upsell = {
      showButton$: this.servicesReady$,
      articleKey,
    };

    if (!this.servicesToShow.some(({ category }) => category === Category.SEAT)) {
      const [first, ...rest] = this.servicesToShow;
      const emptySeat: FinnairServiceItemWithUpsell = {
        category: Category.SEAT,
        bounds: this.booking.bounds.map(({ id }) => ({ id, segments: [], totalPrice: null, quantity: 0 })),
        quantity: 0,
        title: null,
        totalPrice: { amount: '0', currencyCode: first?.totalPrice.currencyCode ?? 'EUR' },
        upsell,
      };
      return [first, emptySeat, ...rest].filter(Boolean);
    } else {
      return this.servicesToShow.map((service) => {
        if (service.category === Category.SEAT) {
          return {
            ...service,
            upsell,
          };
        }
        return service;
      });
    }
  }
}
